import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Video, Clock, Send, Sparkles, Globe, Loader2 } from "lucide-react";
import api, { apiError } from "../../lib/api";
import { Switch } from "../ui/switch";
import { UpcomingStrip } from "./UpcomingStrip";
import { PostDetail } from "./PostDetail";
import { TopicDialog } from "./TopicDialog";
import { MusicPanel } from "./MusicPanel";
import { MetaConnectCard } from "./MetaConnectCard";
import { HistoryGrid } from "./HistoryGrid";
import { BUSY } from "./socialMeta";

const Chip = ({ ok, icon: Icon, label, testId }) => (
  <span data-testid={testId} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border ${ok ? "border-[#A7E3CC] bg-[#E3F5EE] text-[#047857]" : "border-[#F5C9B0] bg-[#FEF1E7] text-[#9A3412]"}`}>
    <Icon className="w-3.5 h-3.5" />{label}
  </span>
);

export default function SocialAgentAdmin() {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [topicFor, setTopicFor] = useState(null);

  const load = useCallback(async () => {
    try { const { data: d } = await api.get("/admin/social/overview"); setData(d); setSelected((s) => s || d.today); }
    catch (e) { toast.error(apiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const busy = data && [...data.upcoming, ...data.history].some((p) => BUSY.includes(p.status));
  useEffect(() => { if (!busy) return undefined; const t = setInterval(load, 5000); return () => clearInterval(t); }, [busy, load]);

  const [scanning, setScanning] = useState(false);
  const save = async (patch, msg) => {
    try { await api.put("/admin/social/settings", patch); toast.success(msg); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const toggle = (enabled) => save({ enabled }, enabled ? "Agent activé" : "Agent en pause");
  const scan = async () => {
    setScanning(true);
    try { const { data: r } = await api.post("/admin/social/scan", {}, { timeout: 120000 }); toast.success(`noldgo.com analysé : ${r.urls} pages, ${r.new} nouveauté(s)`); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setScanning(false); }
  };

  if (!data) return <div data-testid="social-loading" className="text-sm text-[#75807A] p-6">Chargement de l'agent vidéo…</div>;
  const post = [...data.upcoming, ...data.history].find((p) => p.date === selected) || data.upcoming[0];

  return (
    <div data-testid="social-agent-admin" className="space-y-5">
      <header className="bg-[#059669] text-white rounded-2xl p-5 sm:p-6 flex flex-col lg:flex-row lg:items-center gap-4 justify-between">
        <div>
          <h2 className="flex items-center gap-2 text-xl sm:text-2xl font-extrabold"><Video className="w-6 h-6" />Agent vidéo Nold Go</h2>
          <p className="text-sm text-white/85 mt-1 max-w-xl">Chaque jour, un Reel de ~22 s (clip IA + textes Nold Go) est créé à partir de la base et des nouveautés de noldgo.com, puis publié automatiquement.</p>
        </div>
        <label className="flex items-center gap-3 bg-white/10 rounded-xl px-4 py-3 cursor-pointer">
          <Switch checked={data.enabled} onCheckedChange={toggle} data-testid="social-enabled-switch" />
          <span className="text-sm font-semibold">{data.enabled ? "Agent actif" : "Agent en pause"}</span>
        </label>
      </header>
      <div className="flex flex-wrap gap-2">
        <Chip ok icon={Clock} label={`Publication auto à ${data.publish_time} (Bruxelles)`} testId="social-chip-time" />
        <Chip ok={!!data.publisher} icon={Send} testId="social-chip-publisher"
          label={data.publisher ? `${data.meta_direct ? `Meta direct${data.upload_post ? " + secours Upload-Post" : ""}` : "Upload-Post"} : ${[data.facebook && "Facebook", data.instagram && "Instagram"].filter(Boolean).join(" + ") || "aucun réseau connecté"}` : "Publication : connexion Facebook à faire"} />
        {data.publisher && !data.instagram && <span data-testid="social-chip-ig-missing" className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold border border-[#F5C9B0] bg-[#FEF1E7] text-[#9A3412]">Instagram pas encore connecté</span>}
        <label data-testid="social-chip-aiclip" className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold border border-[#ECE7DE] bg-white cursor-pointer">
          <Sparkles className="w-3.5 h-3.5 text-[#F26419]" />Clip IA de fond (Kling)
          <Switch checked={data.ai_clip} onCheckedChange={(v) => save({ ai_clip: v }, v ? "Clip IA activé" : "Clip IA désactivé (montage seul)")} data-testid="social-aiclip-switch" />
        </label>
        <button onClick={scan} disabled={scanning} data-testid="social-scan-btn"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border border-[#ECE7DE] bg-white hover:bg-[#F7F4EF] transition-colors">
          {scanning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Globe className="w-3.5 h-3.5 text-[#059669]" />}
          {data.scan ? `Veille noldgo.com : ${data.novelties} nouveauté(s)` : "Analyser noldgo.com"}
        </button>
        <span data-testid="social-published-count" className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold border border-[#ECE7DE] bg-white">{data.published_count} vidéo(s) publiée(s)</span>
      </div>
      <MetaConnectCard onChange={load} />
      <UpcomingStrip posts={data.upcoming} today={data.today} selected={post?.date} onSelect={setSelected} />
      {post && <PostDetail post={post} metaReady={data.meta_ready} onChangeTopic={() => setTopicFor(post.date)} reload={load} />}
      <div className="grid lg:grid-cols-[1fr_340px] gap-5 items-start">
        <HistoryGrid posts={data.history} onSelect={(p) => { setSelected(p.date); window.scrollTo({ top: 0, behavior: "smooth" }); }} />
        <MusicPanel music={data.music} reload={load} />
      </div>
      <TopicDialog date={topicFor} open={!!topicFor} onClose={() => setTopicFor(null)} onSaved={load} />
    </div>
  );
}
